import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  AppBar,
  Toolbar,
  Stack,
  useTheme,
  useMediaQuery,
  Link,
  Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RainforestTreeIcon from "components/icons/RainforestTreeIcon";
import InfoIcon from "@mui/icons-material/Info";
import GroupsIcon from "@mui/icons-material/Groups";
import HandshakeIcon from "@mui/icons-material/Handshake";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import GavelIcon from "@mui/icons-material/Gavel";
import PolicyIcon from "@mui/icons-material/Policy";
import { MapRainforestMarker, MapRainforestPhoto } from "types/map";
import MapMarkerRainforestVideoEmbed from "./MapMarkerRainforestVideoEmbed";

interface Props {
  open: boolean;
  onClose: () => void;
  marker: MapRainforestMarker;
}

const MapMarkerRainforestInfoDetails = ({ open, onClose, marker }: Props) => {
  const { details, name } = marker;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activePhoto, setActivePhoto] = useState<MapRainforestPhoto | null>(null);

  const renderSection = (
    title: string,
    icon: React.ReactNode,
    content: string | undefined,
    emphasized = false
  ) => {
    if (!content) return null;
    return (
      <Box
        sx={{
          mb: 3,
          ...(emphasized && { bgcolor: "#EAEAEA", p: 2, borderRadius: 1 }),
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
          <Typography
            variant="subtitle1"
            color="text.primary"
            fontWeight="bold"
            sx={{ letterSpacing: 0.5 }}
          >
            {title}
          </Typography>
        </Stack>
        <Box sx={{ pl: isMobile ? 2 : 4 }}>
          <ReactMarkdown
            components={{
              p: ({ node, ...props }: any) => (
                <Typography
                  component="p"
                  variant="body1"
                  color="text.primary"
                  sx={{ mb: 1.5, lineHeight: 1.6 }}
                  {...props}
                />
              ),
              li: ({ node, ...props }: any) => (
                <Typography
                  component="li"
                  variant="body1"
                  color="text.primary"
                  sx={{ mb: 0.5, lineHeight: 1.6 }}
                  {...props}
                />
              ),
              ul: ({ node, ...props }: any) => (
                <Box component="ul" sx={{ pl: 2, mb: 1.5 }} {...props} />
              ),
              ol: ({ node, ...props }: any) => (
                <Box component="ol" sx={{ pl: 2, mb: 1.5 }} {...props} />
              ),
              a: ({ node, ...props }: any) => (
                <Link
                  {...props}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  color="primary"
                />
              ),
            }}
          >
            {content}
          </ReactMarkdown>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? "100%" : "auto",
          maxHeight: isMobile ? "100%" : "90vh",
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
          boxShadow: isMobile ? "none" : "0 12px 40px rgba(0,0,0,0.3)",
        },
      }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: "#2e7d32",
          background: "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Toolbar variant="dense" sx={{ minHeight: isMobile ? 56 : 64, gap: 2 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{ flexGrow: 1, minWidth: 0 }}
          >
            <RainforestTreeIcon sx={{ color: "#a5d6a7", fontSize: 28 }} />
            <Typography
              variant="h6"
              component="div"
              sx={{
                color: "white",
                fontWeight: "bold",
                letterSpacing: 0.5,
                fontSize: isMobile ? "1.1rem" : "1.25rem",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
              }}
            >
              {name}
            </Typography>
          </Stack>

          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            size="medium"
            sx={{
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ p: isMobile ? 2 : 4, bgcolor: "#ffffff" }}>
        {marker.videoUrl ? (
          <Box sx={{ mb: 4, borderRadius: 2, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }}>
            <MapMarkerRainforestVideoEmbed url={marker.videoUrl} />
          </Box>
        ) : marker.picture ? (
          <Box sx={{ mb: 4 }}>
            <Box
              component="img"
              src={import.meta.env.BASE_URL + marker.picture.replace(/^\//, "")}
              alt={name}
              sx={{
                width: "100%",
                maxHeight: isMobile ? 240 : 400,
                objectFit: "cover",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                display: "block",
              }}
            />
            {(marker.pictureDescription || marker.pictureCredit) && (
              <Box sx={{ mt: 1, px: 1 }}>
                {marker.pictureDescription && (
                  <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
                    {marker.pictureDescription}
                  </Typography>
                )}
                {marker.pictureCredit && (
                  <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5, display: "block" }}>
                    Foto: {marker.pictureCredit}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : null}
        {details ? (
          <>
            <Grid container spacing={isMobile ? 0 : 4}>
            <Grid item xs={12} md={6}>
              {renderSection(
                "Partners",
                <GroupsIcon fontSize="medium" color="success" />,
                details.partners
              )}

              {renderSection(
                "Subgrantee/Local partners",
                <GroupsIcon fontSize="medium" color="success" />,
                details.subgrantee
              )}

              {renderSection(
                "Context",
                <InfoIcon fontSize="medium" color="success" />,
                details.context
              )}

              {renderSection(
                "Underlying political conditions",
                <PolicyIcon fontSize="medium" color="warning" />,
                details.politicalConditions
              )}

              {renderSection(
                "Underlying judicial and economic conditions",
                <GavelIcon fontSize="medium" color="warning" />,
                details.judicialEconomicConditions
              )}

            </Grid>
            <Grid item xs={12} md={6}>
              {renderSection(
                "Foreco Contribution",
                <HandshakeIcon fontSize="medium" color="success" />,
                details.forecoContribution,
                true
              )}

              {renderSection(
                "Highlight",
                <EmojiEventsIcon fontSize="medium" color="warning" />,
                details.highlight,
                true
              )}

              {renderSection(
                "Local Impact",
                <VolunteerActivismIcon fontSize="medium" color="success" />,
                details.localImpact,
                true
              )}

            </Grid>
          </Grid>
          
          {/* Region Media Section */}
          {((details.photos && details.photos.length > 0) || (details.videos && details.videos.length > 0)) && (
            <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
              <Typography variant="h6" color="success.800" sx={{ fontWeight: "bold", mb: 3, letterSpacing: 0.5 }}>
                Region Media & Documentaries
              </Typography>
              <Grid container spacing={4}>
                {/* Videos */}
                {details.videos && details.videos.length > 0 && (
                  <Grid item xs={12} md={details.photos && details.photos.length > 0 ? 6 : 12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}>
                      Featured Videos
                    </Typography>
                    <Stack spacing={2}>
                      {details.videos.map((video, idx) => (
                        <Box key={idx} sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                            {video.title}
                          </Typography>
                          <MapMarkerRainforestVideoEmbed url={video.url} />
                          {video.description && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontStyle: "italic" }}>
                              {video.description}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Grid>
                )}
                
                {/* Photos */}
                {details.photos && details.photos.length > 0 && (
                  <Grid item xs={12} md={details.videos && details.videos.length > 0 ? 6 : 12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}>
                      Photo Gallery
                    </Typography>
                    <Grid container spacing={1.5}>
                      {details.photos.map((photo, idx) => (
                        <Grid item xs={6} sm={4} key={idx}>
                          <Box
                            onClick={() => setActivePhoto(photo)}
                            sx={{
                              position: "relative",
                              width: "100%",
                              paddingTop: "75%", // 4:3 ratio
                              borderRadius: 1.5,
                              overflow: "hidden",
                              cursor: "pointer",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                              border: "1px solid",
                              borderColor: "divider",
                              transition: "transform 0.2s, box-shadow 0.2s",
                              "&:hover": {
                                transform: "scale(1.03)",
                                boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                                "& .photo-overlay": { opacity: 1 }
                              }
                            }}
                          >
                            <Box
                              component="img"
                              src={photo.url}
                              alt={photo.title}
                              sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <Box
                              className="photo-overlay"
                              sx={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                width: "100%",
                                background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)",
                                p: 1,
                                boxSizing: "border-box",
                                opacity: { xs: 1, sm: 0 },
                                transition: "opacity 0.2s ease-in-out",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "white",
                                  fontWeight: "bold",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.1,
                                  fontSize: "0.7rem",
                                }}
                              >
                                {photo.title}
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          </>
        ) : (
          <Typography variant="body1" color="text.primary" align="center">
            No detailed information available.
          </Typography>
        )}
      </DialogContent>

      {/* Photo Lightbox */}
      <Dialog
        open={!!activePhoto}
        onClose={() => setActivePhoto(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "black",
            borderRadius: 2,
            overflow: "hidden",
            position: "relative",
            boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
          }
        }}
      >
        <IconButton
          onClick={() => setActivePhoto(null)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            bgcolor: "rgba(0,0,0,0.5)",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
        {activePhoto && (
          <Box sx={{ position: "relative" }}>
            <Box
              component="img"
              src={activePhoto.url}
              alt={activePhoto.title}
              sx={{
                width: "100%",
                maxHeight: "75vh",
                objectFit: "contain",
                display: "block",
                mx: "auto"
              }}
            />
            <Box sx={{ p: 2.5, bgcolor: "rgba(30,30,30,0.95)", color: "white" }}>
              <Typography variant="body1" sx={{ fontWeight: "bold", fontSize: "0.95rem", lineHeight: 1.4 }}>
                {activePhoto.title}
              </Typography>
              {activePhoto.credit && (
                <Typography variant="caption" sx={{ color: "grey.400", mt: 0.75, display: "block" }}>
                  Foto: {activePhoto.credit}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Dialog>
    </Dialog>
  );
};

export default MapMarkerRainforestInfoDetails;